import { ReloadIcon } from '@radix-ui/react-icons'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-dynamicBlue text-staticWhite border border-transparent hover:bg-dynamicBlue/90',
        secondary:
          'bg-[rgba(250,250,250,0.05)] text-neutralColor border border-neutralColor5 hover:bg-[rgba(250,250,250,0.1)]',
        outline:
          'bg-baseColorHighlight text-neutralColor border border-transparent hover:bg-[rgba(250,250,250,0.05)]',
        ghost:
          'text-neutralColor border border-transparent hover:bg-baseColorHighlight',
      },
      size: {
        default: 'h-9 px-4 text-sm gap-2',
        sm: 'h-8 px-3 text-sm gap-2',
        xs: 'h-6 px-2 text-xs gap-1.5',
        icon: 'h-9 w-9',
      },
      rounded: {
        true: 'rounded-full',
        false: 'rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      rounded: false,
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const ButtonLoading = ({
  className,
  variant,
  size,
  rounded,
  children,
}: {
  className: string | undefined
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  rounded?: ButtonProps['rounded']
  children: React.ReactNode
}) => (
  <Button
    disabled
    variant={variant}
    size={size}
    rounded={rounded}
    className={cn('relative', className)}
  >
    <span className="opacity-0">{children}</span>
    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      <ReloadIcon className="h-4 w-4 animate-spin" />
    </span>
  </Button>
)

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      isLoading,
      disabled,
      onClick,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    if (isLoading) {
      return (
        <ButtonLoading
          className={className}
          variant={variant}
          size={size}
          rounded={rounded}
        >
          {children}
        </ButtonLoading>
      )
    }

    const content = asChild ? (
      children
    ) : (
      <>
        {leftIcon}
        {children}
        {rightIcon}
      </>
    )

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded }), className)}
        ref={ref}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        {...props}
      >
        {content}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
